// Portal Missed Orders API — Merlin's per-order findings and the human
// correction loop (kanban fc6bca28). Reads the same rows PJ pulls; the one
// write is a correction row ("that is wrong, it was X because Y").

import api from './api';

export type CorrectionField =
  | 'cause_category'
  | 'merlin_subcategory'
  | 'responsible_party'
  | 'is_miss'
  | 'pj_category';

export type KbWriteStatus = 'pending' | 'queued' | 'updated' | 'deleted' | 'failed';

export type CorrectionStatus =
  | 'recorded'
  | 'challenge_open'
  | 'challenge_upheld'
  | 'challenge_declined'
  | 'withdrawn';

/** One Merlin finding, the export columns (GET /api/external/pj-pyomo/findings shape). */
export interface MissedOrderFindingRow {
  finding_id: string;
  source_kind: string;
  source_ref: string;
  run_date: string;
  model: string | null;
  produced_at: string | null;
  sales_order_no: string;
  line_key: string | null;
  customer_no: string | null;
  customer_name: string | null;
  in_pj_miss_list: boolean | null;
  pj_grade: string | null;
  pj_reason: string | null;
  pj_list_source: string | null;
  cause_category: string;
  merlin_subcategory: string | null;
  responsible_party: string | null;
  agrees_with_pj: boolean | null;
  is_miss: boolean | null;
  explanation: string;
  confidence: string;
  confidence_score: number | null;
  why_flagged: string | null;
  discussed_in_meeting: boolean | null;
  new_connection: boolean | null;
  evidence_count: number;
  evidence_refs: string;
  open_question: string | null;
}

export interface MissedOrderCorrection {
  id: string;
  finding_id: string;
  investigation_id: string | null;
  sales_order_no: string;
  customer_no: string | null;
  customer_name: string | null;
  run_date: string;
  field: CorrectionField;
  merlin_value: string | null;
  human_value: string;
  reason: string;
  pj_grade_at_time: string | null;
  corrected_by_user_id: string | null;
  corrected_by_email: string | null;
  corrected_by_name: string | null;
  corrected_at: string;
  surface: string;
  status: CorrectionStatus;
  resolved_by_email: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  withdrawn_at: string | null;
  lesson_text: string;
  /** The Knowledge Bank loop: where the lesson stands in Merlin's playbook bank. */
  kb_entry_id: string | null;
  kb_write_status: KbWriteStatus;
  kb_write_error: string | null;
  kb_synced_at: string | null;
}

export interface FindingWithCorrections {
  finding: MissedOrderFindingRow;
  corrections: MissedOrderCorrection[];
}

export interface FindingsWithCorrectionsResponse {
  rows: FindingWithCorrections[];
  total: number;
  corrected: number;
  challenges_open: number;
}

export interface VocabOption {
  value: string;
  label: string;
}

export interface CorrectionOptions {
  fields: VocabOption[];
  cause_categories: VocabOption[];
  responsible_parties: VocabOption[];
  pj_categories: VocabOption[];
  is_miss: VocabOption[];
  max_reason_chars: number;
}

export interface CorrectionCreate {
  field: CorrectionField;
  human_value: string;
  reason: string;
  surface?: 'portal_missed_orders' | 'csv_reply' | 'chat' | 'api';
}

export interface FindingsQuery {
  run_date_from?: string;
  run_date_to?: string;
  sales_order_no?: string;
  source_kind?: string;
  limit?: number;
}

export interface CorrectionListResponse {
  corrections: MissedOrderCorrection[];
  total: number;
}

const BASE = '/portal/missed-orders';

export const portalMissedOrdersService = {
  getOptions: async (): Promise<CorrectionOptions> => {
    const res = await api.get<CorrectionOptions>(`${BASE}/options`);
    return res.data;
  },

  listFindings: async (query: FindingsQuery = {}): Promise<FindingsWithCorrectionsResponse> => {
    const params: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') params[k] = v;
    }
    const res = await api.get<FindingsWithCorrectionsResponse>(`${BASE}/findings`, { params });
    return res.data;
  },

  /** The one-click "that is wrong, it was X because Y". 201 with the row. */
  correct: async (findingId: string, body: CorrectionCreate): Promise<MissedOrderCorrection> => {
    const res = await api.post<MissedOrderCorrection>(
      `${BASE}/findings/${encodeURIComponent(findingId)}/corrections`,
      { surface: 'portal_missed_orders', ...body },
    );
    return res.data;
  },

  listCorrections: async (status?: CorrectionStatus): Promise<CorrectionListResponse> => {
    const res = await api.get<CorrectionListResponse>(`${BASE}/corrections`, {
      params: status ? { status } : {},
    });
    return res.data;
  },

  withdraw: async (correctionId: string): Promise<MissedOrderCorrection> => {
    const res = await api.post<MissedOrderCorrection>(
      `${BASE}/corrections/${encodeURIComponent(correctionId)}/withdraw`,
    );
    return res.data;
  },

  /** Admin: run the playbook-bank write for a correction now (retry after a failure). */
  kbSync: async (correctionId: string): Promise<MissedOrderCorrection> => {
    const res = await api.post<MissedOrderCorrection>(
      `${BASE}/corrections/${encodeURIComponent(correctionId)}/kb-sync`,
    );
    return res.data;
  },

  /** Admin: PJ's answer to a challenge of his category, entered on his word. */
  resolve: async (
    correctionId: string,
    outcome: 'upheld' | 'declined',
    note?: string,
  ): Promise<MissedOrderCorrection> => {
    const res = await api.post<MissedOrderCorrection>(
      `${BASE}/corrections/${encodeURIComponent(correctionId)}/resolve`,
      { outcome, note: note || null },
    );
    return res.data;
  },
};

/** The message the API returned for a refused correction, else a generic line. */
export function correctionErrorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string };
    if (first?.msg) return first.msg;
  }
  return 'The correction could not be saved.';
}
